import find from 'lodash/find';
import pick from 'lodash/pick';
import { parse } from 'query-string';
import { REHYDRATE } from 'redux-persist';
import tinycolor2 from 'tinycolor2';

import * as types from '../actions/actionTypes';
import {
    interpolateSlider,
    parseToHexString,
} from '../helper/ColorUtils';
import { updateEntityItemsCache, errorMessage } from '../helper/entityReducer';
import { getCacheItem } from './queryCache';
import { getCurrentPageNumber } from './page';
import { getPatternPerPage } from './ui.patterngallery';
import { getCurrentLangCode } from './i18n';
import { getCurrentSearchValue } from './ui.sitesearch';
import { getCurrentRouteDesignerId, getDesigners } from './designer';

const patterns = (state, query, index, action) => {
    let newColors = [];
    let pattern;
    let currentColors = [];
    let initialColors = [];
    let newEntities = {};

    if (action) {
        if (action.patternid > 0 || action.colors) {
            pattern = getPatternFromReducerState(state, action.patternid);
        }
    }

    if (pattern && pattern.initialColors && pattern.initialColors.length > 0) {
        initialColors = pattern.initialColors.slice(0);
        currentColors = pattern.initialColors.slice(0);
    }

    if (pattern && pattern.colors && pattern.colors.length) {
        currentColors = pattern.colors.slice(0);
    }

    switch (action.type) {

        case types.PATTERNS_REQUESTED:
            return Object.assign({}, state, {
                isFetching: true,
                query: action.query,
            });

        case types.PATTERNS_RECEIVED:
            return Object.assign({}, state,
                updateEntityItemsCache(state, action, 'patterns'),
            );

        case types.PATTERNS_REQUEST_FAILED:
            return Object.assign({}, state, {
                isFetching: false,
                errorMessage: errorMessage(action),
            });

        case types.PATTERNS_DID_INVALIDATE:
            return state;

        case types.COLORS_RECEIVED:
            return mergeColorData(state, action);

        case types.COLORS_DID_INVALIDATE:
            return {
                colors: [],
            };

        case types.COLORS_RESETTED:
            if (action && action.colors && action.colors.length > 0) {
                newColors = action.colors.map(color => tinycolor2(color).toHsl());
            }
            else {
                newColors = initialColors;
            }
            pattern.colors = newColors;
            newEntities =
                Object.assign({}, state.entities, { [action.patternid]: pattern });
            return Object.assign({}, state, { entities: newEntities });

        case types.COLOR_CHANGED:
            newColors = currentColors;
            newColors[index] = action.color;
            pattern.colors = newColors;
            newEntities =
                Object.assign({}, state.entities, { [action.patternid]: pattern });
            return Object.assign({}, state, { entities: newEntities });

        case types.SLIDER_HUE_CHANGED:
            newColors = currentColors.map(color => {
                const newColor = Object.assign({},
                    color,
                    { h: parseInt(action.color, 10) });
                return newColor;
            });
            pattern.colors = newColors;
            newEntities =
                Object.assign({}, state.entities, { [action.patternid]: pattern });
            return Object.assign({}, state, { entities: newEntities });

        case types.SLIDER_SATURATION_CHANGED:
            newColors = currentColors.map((color, index) => {
                const newColor = Object.assign({},
                    color,
                    { s: interpolateSlider(initialColors[index].s, action.color) });
                return newColor;
            });
            pattern.colors = newColors;
            newEntities =
                Object.assign({}, state.entities, { [action.patternid]: pattern });
            return Object.assign({}, state, { entities: newEntities });

        case types.SLIDER_LIGHTNESS_CHANGED:
            newColors = currentColors.map((color, index) => {
                const newColor = Object.assign({},
                    color,
                    { l: interpolateSlider(initialColors[index].l, action.color) });
                return newColor;
            });
            pattern.colors = newColors;
            newEntities =
                Object.assign({}, state.entities, { [action.patternid]: pattern });
            return Object.assign({}, state, { entities: newEntities });

        case REHYDRATE:
            return state;

        default:
            return state;
    }
};

export default patterns;

const getPatternFromReducerState = (state, patternid) => {
    if (state.items.length === 0) {
        return {};
    }
    return find(state.entities, { id: patternid });
};

/**
 * Get a single pattern by patternid.
 *
 * @param state
 * @param patternid
 * @returns {{}}
 *  A single pattern object.
 */
export const getPattern = (state, patternid) => {
    if (state.stolo.entities.patterns.items.length === 0) {
        return {};
    }
    return find(state.stolo.entities.patterns.entities, { id: patternid });
};

/**
 * Get all patterns.
 *
 * @param state
 * @returns {{}}
 *  Object with patterns keyed by id.
 */
export const getPatterns = state => {
    if (state.stolo.entities.patterns.items.length === 0) {
        return {};
    }
    return state.stolo.entities.patterns.entities;
};

/**
 * Get a defined number of patterns.
 *
 * @param state
 * @param count
 * @returns {{}}
 *  Object with $count patterns keyed by id.2
 */
export const getXPatterns = (state, count) => {
    if (state.stolo.entities.patterns.items.length === 0) {
        return {};
    }
    return Object.entries(state.stolo.entities.patterns.entities).reverse().slice(0, count);
};

export const getIsFetchingPatterns = state => state.stolo.entities.patterns.isFetching;

/**
 * Get all patterns by ids.
 *
 * @param state
 *  the state object
 * @param ids,
 *      array of ids
 * @returns {{}}
 *  Object with patterns keyed by id.
 */
export const getPatternsByIds = (state, ids = []) => {
    if (state.stolo.entities.patterns.items.length === 0) {
        return {};
    }
    return pick(state.stolo.entities.patterns.entities, ids);
};

/**
 * Load patterns from cache.
 *
 * @param state
 *      The state object.
 * @param query
 *      The query object for fetching the cache item for.
 *
 * @returns {{}}
 */
const getPatternsFromQuery = (state, query) => {
    const cacheItem = getCacheItem(state, query);
    if (cacheItem) {
        return getPatternsByIds(state, cacheItem.items);
    }
    return [];
};

/**
 * Load patterns from cache.
 *
 * @param state
 *      The state object.
 * @param query
 *      The query object for fetching the cache item for.
 *
 * @returns {{}}
 */
const getItemsFromQuery = (state, query) => {
    const cacheItem = getCacheItem(state, query);
    if (cacheItem) {
        return cacheItem.items;
    }
    return [];
};

/**
 * Load patterns from cache.
 *
 * @param state
 *      The state object.
 * @param query
 *      The query object for fetching the cache item for.
 *
 * @returns {{}}
 */
const getTotalFromQuery = (state, query) => {
    const cacheItem = getCacheItem(state, query);
    if (cacheItem) {
        return cacheItem.totalHits;
    }
    return null;
};

/**
 * Returns the newest patterns.
 *
 * @param state
 * @returns {{}}
 */
export const getNewestPatterns = state => {
    const page = getCurrentPageNumber(state);
    const count = getPatternPerPage(state);
    const language = getCurrentLangCode(state);

    const query = {
        language,
        limit: count,
        offset: page ? (page - 1) * count : 0,
        sort: 'newestFirst',
    };

    return getPatternsFromQuery(state, query);
};

export const getSearchedPatterns = state => {
    const page = getCurrentPageNumber(state);
    const limit = getPatternPerPage(state);
    const language = getCurrentLangCode(state);
    const search = getCurrentSearchValue(state);

    const query = {
        language,
        limit,
        offset: page ? (page - 1) * limit : 0,
        text: search,
    };

    return getPatternsFromQuery(state, query);
};

/**
 * Returns the newest patterns.
 *
 * @param state
 * @returns {{}}
 */
export const getCategoryPatterns = state => {
    const page = getCurrentPageNumber(state);
    const count = getPatternPerPage(state);
    const language = getCurrentLangCode(state);
    const categoryid = state.location.payload.categoryid;

    const query = {
        language,
        limit: count,
        offset: page ? (page - 1) * count : 0,
        sort: 'newestFirst',
        categoryIds: categoryid,
    };

    return getPatternsFromQuery(state, query);
};

/**
 * Returns the cached patterns per uri.
 *
 * @param state
 * @returns {{patterns: {}, totalHits: {}}}
 */
export const getGalleryData = state => {
    const page = getCurrentPageNumber(state);
    const limit = getPatternPerPage(state);
    const language = getCurrentLangCode(state);
    const locationType = state.location.type;
    var query = {};

    if (locationType === types.NAVIGATED_TO_PATTERNS_FROM_DESIGNER) {
        const designerid = getCurrentRouteDesignerId(state);
        query = {
            language,
            sort: 'newestFirst',
            limit,
            offset: page ? (page - 1) * limit : 0,
            designerIds: designerid,
        };
    }

    if (locationType === types.NAVIGATED_TO_NEWEST_PATTERNGALLERY) {
        query = {
            language,
            sort: 'newestFirst',
            limit,
            offset: page ? (page - 1) * limit : 0,
        };
    }

    if (locationType === types.NAVIGATED_TO_CATEGORIE_PATTERNS_GALLERY) {
        const categoryid = state.location.payload.categoryid;
        query = {
            language,
            limit,
            offset: page ? (page - 1) * limit : 0,
            sort: 'newestFirst',
            categoryIds: categoryid,
        };
    }

    if (locationType === types.NAVIGATED_TO_SEARCHGALLERY) {
        const search = getCurrentSearchValue(state);
        query = {
            language,
            limit,
            offset: page ? (page - 1) * limit : 0,
            text: search,
        };
    }

    return {
        patterns: getPatternsFromQuery(state, query),
        items: getItemsFromQuery(state, query),
        totalHits: getTotalFromQuery(state, query),
    };
};

/**
 * Get thumbnails from a pattern.
 *
 * @param state
 * @param patternid
 * @returns {*}
 */
export const getPatternThumbnails = (state, patternid) => {
    let thumbnails;
    const pattern = getPattern(state, patternid);
    if (pattern && pattern.thumbnails) {
        thumbnails = pattern.thumbnails;
    }
    return thumbnails;
};

export const getPatternThumbnail = (state, patternid, sizeInPx) => {
    let thumb;
    const thumbnails = getPatternThumbnails(state, patternid);
    if (thumbnails) {
        if (sizeInPx === 400) {
            thumb = thumbnails.medium.url;
        }
        else if (sizeInPx > 400) {
            thumb = thumbnails.xlargeDiscreet.url;
        }
        else {
            thumb = thumbnails.gallery.url;
        }
    }
    return thumb;
};

/**
 * Returns the current colors of a pattern. Defaults for initialColors if no
 * custom colors are available.
 *
 * @param state
 * @param patternid
 * @returns {*}
 */
export const getPatternColors = (state, patternid) => {
    try {
        const pattern = getPattern(state, patternid);
        return pattern.colors &&
        pattern.colors.length >
        0 ?
            pattern.colors :
            getPatternInitialColors(state, patternid);
    }
    catch (e) {
        console.error(`getPatternColors: Pattern not found: ${patternid}`);
        return [];
    }
};

/**
 * Takes a color value and returns a hex string value.
 *
 * @param state
 * @param patternid
 * @param swatchid
 * @returns {*}
 */
export const getSwatchColor = (state, patternid, swatchid) => {
    const colors = getPatternColors(state, patternid);
    return parseToHexString(colors[swatchid]);
};

/**
 * Returns the initialColors of a pattern.
 *
 * @param state
 * @param patternid
 * @returns {Array|*}
 */
export const getPatternInitialColors = (state, patternid) => {
    isColorable(state, patternid);
    const pattern = getPattern(state, patternid);
    return pattern.initialColors || [];
};

/**
 * Checks if pattern is recolorable.
 *
 * @param state
 * @param patternid
 * @returns {*}
 */
export const isColorable = (state, patternid) => {
    try {
        const pattern = getPattern(state, patternid);
        return pattern.metadata.recolorable;
    }
    catch (e) {
        console.error(`isColorable: Pattern not found: ${patternid}`);
        return [];
    }
};

const mergeColorData = (state, action) => {
    // Parse the query string to extract the patternId.
    const query = parse(action.query);
    const patternid = parseInt(query.id, 10);
    const index = state.items.indexOf(patternid);
    let colors = [];

    if (index > -1) {
        const pattern = state.entities[patternid];
        colors = action.response.map(color => tinycolor2(color).toHsl());
        pattern.initialColors = colors;
        const newEntities = Object.assign({},
            state.entities,
            { [action.patternid]: pattern });
        return Object.assign({}, state, { entities: newEntities });
    }
};

/**
 * Get the patterns title.
 *
 * @param state
 * @param patternid
 * @returns {*}
 */
export const getPatternTitle = (state, patternid) => {
    try {
        const pattern = getPattern(state, patternid);
        return pattern.title;
    }
    catch (e) {
        console.error(`getPatternTitle: Pattern not found: ${patternid}`);
        return [];
    }
};

/**
 * Get the patterns designer.
 *
 * @param state
 * @param patternid
 * @returns {*}
 */
export const getPatternDesigner = (state, patternid) => {
    try {
        const pattern = getPattern(state, patternid);
        const designer = getDesigners(state);
        return designer[parseInt(pattern.designer, 10)] || 'patterndesigns.com';
    }
    catch (e) {
        console.error(`getPatternDesigner: DesignerData not available: ${patternid}`);
        return 'patterndesigns.com';
    }
};




// WEBPACK FOOTER //
// ./src/reducers/patterns.js